import type { RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type MockTestRecord = RaRecord & {
    id: string;
    [key: string]: unknown;
};

export type MockTestListParams = {
    pageNo: number;
    pageSize: number;
    examName?: string;
    mockTestKind?: string;
    examTaskCategory?: string;
    isPublishedToTarget?: boolean;
    sort?: string;
    order?: 'ASC' | 'DESC';
};

export type GenerateMockTestParams = {
    examName: string;
    generationProfileId: string;
    mockTestKind: string;
    sectionCategory?: string;
    isFree: boolean;
};

export type GenerationProfileListItem = RaRecord & {
    id: string;
    generationProfileId: string;
    name: string;
    examName: string;
    sectionCount: number;
    createdAt?: string;
    updatedAt?: string;
};

export type GenerationProfileGroup = RaRecord & {
    id: string;
    generationProfileGroupId: string;
    sequenceNumber: number;
    examTaskType: string;
    plannedItemCount: number;
};

export type GenerationProfileSection = RaRecord & {
    id: string;
    generationProfileSectionId: string;
    sequenceNumber: number;
    sectionCategory: string;
    taskTypes: GenerationProfileGroup[];
};

export type GenerationProfileDetail = RaRecord & {
    id: string;
    generationProfileId: string;
    name: string;
    examName: string;
    createdAt?: string;
    updatedAt?: string;
    sections: GenerationProfileSection[];
};

export type GenerationProfileUpsertParams = {
    name: string;
    examName: string;
    sections: Array<{
        sectionCategory: string;
        taskTypes: Array<{
            examTaskType: string;
            plannedItemCount: number;
        }>;
    }>;
};

export type UpdateMockTestParams = {
    isFree: boolean;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type MockTestPublishIssue = {
    code?: string;
    message?: string;
    sourceExamTaskId?: string | null;
    sourceExamTaskType?: string | null;
    targetExamTaskId?: string | null;
    targetExamTaskType?: string | null;
    questionNo?: string | null;
};

export type MockTestTargetSyncResult = {
    id?: string;
    mockTestId?: string;
    title?: string;
    slug?: string;
    sourceEnvironment?: string;
    targetEnvironment?: string;
    sourceStatus?: string;
    targetStatus?: string;
    sectionCount?: number;
    itemGroupCount?: number;
    itemCount?: number;
    examTaskCount?: number;
    issues?: MockTestPublishIssue[];
};

export type MockTestTargetSyncResponse = AdminApiEnvelope<MockTestTargetSyncResult>;

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin mock test request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function normalizeMockTestRecord(item: unknown, fallbackId?: string): MockTestRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.mockTestId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Mock test item is missing an id');
    }

    return {
        ...record,
        id: String(rawId),
    };
}

async function requestAdminMockTest<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminMockTest/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin mock test request failed'));
        }
    }

    return payload as T;
}

async function requestAdminMockTestEnvelope<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<AdminApiEnvelope<T> | T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminMockTest/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as AdminApiEnvelope<T> | T;
}

export async function getMockTestList(params: MockTestListParams) {
    const response = unwrapAdminApiPayload(
        await requestAdminMockTest<
            | {
                  total?: number;
                  items?: unknown[];
              }
            | AdminApiEnvelope<{
                  total?: number;
                  items?: unknown[];
              }>
        >('get-list', { method: 'GET' }, {
            pageNo: String(params.pageNo),
            pageSize: String(params.pageSize),
            ...(params.examName ? { examName: params.examName } : {}),
            ...(params.mockTestKind ? { mockTestKind: params.mockTestKind } : {}),
            ...(params.examTaskCategory ? { examTaskCategory: params.examTaskCategory } : {}),
            ...(params.isPublishedToTarget !== undefined ? { isPublishedToTarget: String(params.isPublishedToTarget) } : {}),
            ...(params.sort ? { sort: params.sort } : {}),
            ...(params.order ? { order: params.order } : {}),
        }),
    );

    const items = Array.isArray(response.items) ? response.items : [];
    return {
        data: items.map((item) => normalizeMockTestRecord(item)),
        total: typeof response.total === 'number' ? response.total : items.length,
    };
}

export async function getMockTest(id: string) {
    const item = unwrapAdminApiPayload(
        await requestAdminMockTest<unknown | AdminApiEnvelope<unknown>>(`get-detail/${id}`, {
            method: 'GET',
        }),
    );

    return { data: normalizeMockTestRecord(item, id) };
}

export async function updateMockTest(id: string, params: UpdateMockTestParams) {
    return unwrapAdminApiPayload(
        await requestAdminMockTest<boolean | AdminApiEnvelope<boolean>>(`update/${id}`, {
            method: 'POST',
            body: JSON.stringify(params),
        }),
    );
}

export async function updateMockTestTitle(id: string, title: string) {
    const body = JSON.stringify({ title });

    return unwrapAdminApiPayload(
        await requestAdminMockTest<boolean | AdminApiEnvelope<boolean>>(`update-title/${id}`, {
            method: 'POST',
            body,
        }),
    );
}

export async function archiveMockTest(id: string) {
    await requestAdminMockTest(`archive/${id}`, { method: 'POST' });
}

export async function activateMockTest(id: string) {
    await requestAdminMockTest(`publish/${id}`, { method: 'POST' });
}

export async function validatePublishMockTest(id: string) {
    return requestAdminMockTestEnvelope<MockTestTargetSyncResult>(`validate-publish/${id}`, { method: 'POST' });
}

export async function publishMockTestToTarget(id: string) {
    return requestAdminMockTestEnvelope<MockTestTargetSyncResult>(`publish-target/${id}`, { method: 'POST' });
}

export async function republishMockTestToTarget(id: string) {
    return requestAdminMockTestEnvelope<MockTestTargetSyncResult>(`republish-target/${id}`, { method: 'POST' });
}

export async function unpublishMockTest(id: string) {
    await requestAdminMockTest(`unpublish/${id}`, { method: 'POST' });
}

export async function deleteMockTest(id: string) {
    await requestAdminMockTest(`delete/${id}`, { method: 'DELETE' });
}

export async function generateMockTest(params: GenerateMockTestParams) {
    return unwrapAdminApiPayload(
        await requestAdminMockTest<unknown | AdminApiEnvelope<unknown>>('generate', {
            method: 'POST',
            body: JSON.stringify(params),
        }),
    );
}

function normalizeGenerationProfileListItem(item: unknown): GenerationProfileListItem {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.generationProfileId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Generation profile item is missing an id');
    }

    return {
        ...record,
        id: String(rawId),
        generationProfileId: String(record.generationProfileId ?? rawId),
        name: String(record.name ?? ''),
        examName: String(record.examName ?? ''),
        sectionCount: Number(record.sectionCount ?? 0),
        createdAt: typeof record.createdAt === 'string' ? record.createdAt : undefined,
        updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : undefined,
    };
}

function normalizeGenerationProfileDetail(item: unknown, fallbackId?: string): GenerationProfileDetail {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.generationProfileId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Generation profile detail is missing an id');
    }

    const sections = Array.isArray(record.sections)
        ? record.sections.map((section) => {
              const sectionRecord = (section ?? {}) as Record<string, unknown>;
              const rawSectionId = sectionRecord.id ?? sectionRecord.generationProfileSectionId;

              if (rawSectionId === undefined || rawSectionId === null || rawSectionId === '') {
                  throw new Error('Generation profile section is missing an id');
              }

              const taskTypes = Array.isArray(sectionRecord.taskTypes)
                  ? sectionRecord.taskTypes.map((group) => {
                        const groupRecord = (group ?? {}) as Record<string, unknown>;
                        const rawGroupId = groupRecord.id ?? groupRecord.generationProfileGroupId;

                        if (rawGroupId === undefined || rawGroupId === null || rawGroupId === '') {
                            throw new Error('Generation profile task type is missing an id');
                        }

                        return {
                            ...groupRecord,
                            id: String(rawGroupId),
                            generationProfileGroupId: String(groupRecord.generationProfileGroupId ?? rawGroupId),
                            sequenceNumber: Number(groupRecord.sequenceNumber ?? 0),
                            examTaskType: String(groupRecord.examTaskType ?? ''),
                            plannedItemCount: Number(groupRecord.plannedItemCount ?? 0),
                        } satisfies GenerationProfileGroup;
                    })
                  : [];

              return {
                  ...sectionRecord,
                  id: String(rawSectionId),
                  generationProfileSectionId: String(sectionRecord.generationProfileSectionId ?? rawSectionId),
                  sequenceNumber: Number(sectionRecord.sequenceNumber ?? 0),
                  sectionCategory: String(sectionRecord.sectionCategory ?? ''),
                  taskTypes,
              } satisfies GenerationProfileSection;
          })
        : [];

    return {
        ...record,
        id: String(rawId),
        generationProfileId: String(record.generationProfileId ?? rawId),
        name: String(record.name ?? ''),
        examName: String(record.examName ?? ''),
        createdAt: typeof record.createdAt === 'string' ? record.createdAt : undefined,
        updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : undefined,
        sections,
    };
}

export async function getGenerationProfileList(examName?: string) {
    const items = unwrapAdminApiPayload(
        await requestAdminMockTest<unknown[] | AdminApiEnvelope<unknown[]>>('generation-profile/list', { method: 'GET' }, {
            ...(examName ? { examName } : {}),
        }),
    );

    const list = Array.isArray(items) ? items : [];
    return list.map((item) => normalizeGenerationProfileListItem(item));
}

export async function getGenerationProfileDetail(id: string) {
    const item = unwrapAdminApiPayload(
        await requestAdminMockTest<unknown | AdminApiEnvelope<unknown>>(`generation-profile/detail/${id}`, {
            method: 'GET',
        }),
    );

    return normalizeGenerationProfileDetail(item, id);
}

export async function createGenerationProfile(params: GenerationProfileUpsertParams) {
    const item = unwrapAdminApiPayload(
        await requestAdminMockTest<unknown | AdminApiEnvelope<unknown>>('generation-profile/create', {
            method: 'POST',
            body: JSON.stringify(params),
        }),
    );

    return normalizeGenerationProfileDetail(item);
}

export async function updateGenerationProfile(id: string, params: GenerationProfileUpsertParams) {
    const item = unwrapAdminApiPayload(
        await requestAdminMockTest<unknown | AdminApiEnvelope<unknown>>(`generation-profile/update/${id}`, {
            method: 'POST',
            body: JSON.stringify(params),
        }),
    );

    return normalizeGenerationProfileDetail(item, id);
}

export async function deleteGenerationProfile(id: string) {
    await requestAdminMockTest(`generation-profile/delete/${id}`, { method: 'DELETE' });
}